import React, {useState} from 'react';

import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ChemicalFormatType, EMPTY_MOLFILE} from 'chemistry/ChemistryConstants';
import {
  ChemicalStructureDrawerMode,
  useControlledChemicalStructureDrawer,
} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {MarvinChemicalFormat} from 'chemistry/marvinjs-wrappers/MarvinJSConstants';
import ChemicalStructureModalInput from 'chemistry/molecules/ChemicalStructureModalInput';
import * as MoleculeActions from 'chemistry/molecules/MoleculeActions';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import notifications from 'notifications';
import CreateBioentityTagInputs, {
  useCreateBioentityTagInputs,
} from 'registry/create-bioentity/CreateBioentityTagInputs';
import {
  create as createBioentity,
  CreateBioentityArgs,
  getDefaultCreateBioentityArgs,
} from 'registry/create-bioentity/CreateBioentityUtil';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import StudySearchInput, {StudySearchItem} from 'studies/StudySearchInput';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import {ButtonSize} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {ExpandableModal} from 'ui-library/modals/ExpandableModal';
import * as net from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

import {stripSaltsAndNotify} from './MoleculeUtil';

interface CreateMoleculeModalProps {
  initialFolderId: string | null;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

export default function CreateMoleculeModal({
  initialFolderId,
  onRequestClose,
  onRequestSubmit,
}: CreateMoleculeModalProps): JSX.Element {
  const [name, setName] = useState<string>('');
  const [folderId, setFolderId] = useState(
    initialFolderId ?? FolderDefaults.get(FolderDefaultsKey.INVENTORY)
  );
  const [sketcherValue, setSketcherValue] = useControlledChemicalStructureDrawer();
  const [posting, setPosting] = useState(false);
  const [studySearchItems, setStudySearchItems] = useState<Array<StudySearchItem>>([]);

  const disabled = name.trim().length === 0 || folderId == null || posting;

  const [tagSchema, tags, schemaSelectorProps, tagInputsProps] = useCreateBioentityTagInputs();

  async function createMolecule(): Promise<void> {
    assert(folderId != null, 'Unexpected creating molecule with null folderId');
    setPosting(true);
    const createBioentityArgs: CreateBioentityArgs = {
      ...getDefaultCreateBioentityArgs(),
      name,
      folderId,
      tagSchema,
      tags,
      studySearchItems,
    };
    createBioentityArgs.chemicalSketcherValue = sketcherValue;
    try {
      if (tagSchema != null) {
        await withLoadingScreen(createBioentity(createBioentityArgs), 'Creating molecule...').then(() =>
          sketcherValue != null
            ? stripSaltsAndNotify(ChemicalFormatType.MOLFILE, sketcherValue)
            : Promise.resolve()
        );
      } else {
        await withLoadingScreen(
          MoleculeActions.createMolecule(
            name,
            folderId,
            ChemicalFormatType.MOLFILE,
            sketcherValue ?? EMPTY_MOLFILE,
            null,
            studySearchItems.map(({id}) => id)
          ).then(() =>
            sketcherValue != null
              ? stripSaltsAndNotify(ChemicalFormatType.MOLFILE, sketcherValue)
              : Promise.resolve()
          ),
          'Creating molecule...'
        );
      }
      FolderDAOCache.invalidateFolderContents(folderId);
      onRequestSubmit();
    } catch (err) {
      setPosting(false);
      if (net.isServerRequestError(err)) {
        notifications.notifyError(err);
      } else {
        throw err;
      }
    }
  }

  return (
    <ExpandableModal
      title="Create molecule"
      buttons={[{disabled, submit: true, text: 'Create'}]}
      onRequestClose={onRequestClose}
      onRequestSubmit={createMolecule}
      startMaximized={true}
      analyticsModule={ANALYTICS_MODULE.CHEMISTRY}
      analyticsModalType="create_molecule"
    >
      <Row>
        <Col sm={12}>
          <FormGroup>
            <FieldLabel label="Entity name" isRequired={true} />
            <input
              autoFocus={true}
              type="text"
              value={name}
              placeholder="Name"
              onChange={(e) => {
                setName(e.target.value);
              }}
              {...getTestProps({component: 'CreateMoleculeModal', element: 'input', key: 'entityName'})}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col sm={BenchlingConfig.STUDIES_ENABLE ? 6 : 12}>
          <FormGroup>
            <FieldLabel label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} isRequired={true} />
            <FolderSelector
              buttonSize={ButtonSize.MEDIUM}
              enableRedesign={true}
              showInventory={true}
              value={folderId}
              onChange={(e) => {
                setFolderId(e.target.value);
              }}
              unsetOnFetchFail={true}
            />
          </FormGroup>
        </Col>
        {BenchlingConfig.STUDIES_ENABLE && (
          <Col sm={6}>
            <FormGroup>
              <FieldLabel label="Studies" />
              <StudySearchInput
                onChange={(studySearchItems: Array<StudySearchItem>) => {
                  setStudySearchItems(studySearchItems);
                }}
                shouldAllowMultiple={true}
                selectedItems={studySearchItems}
              />
            </FormGroup>
          </Col>
        )}
      </Row>
      <Row>
        <Col sm={12}>
          <FormGroup>
            <FieldLabel label="Schema" />
            <TagSchemaSelector
              allowNone={true}
              oligoAwareFolderItemTypes={[ModelTypeConstants.MOLECULE]}
              {...schemaSelectorProps}
            />
          </FormGroup>
          <FormGroup>
            <CreateBioentityTagInputs {...tagInputsProps} />
          </FormGroup>
        </Col>
      </Row>
      <ChemicalStructureModalInput
        initialSketcherValue={EMPTY_MOLFILE}
        onSketcherValueChange={setSketcherValue}
        mode={ChemicalStructureDrawerMode.BASIC_SEARCH}
        inputFormat={null}
        outputFormat={MarvinChemicalFormat.MOLV3}
      />
    </ExpandableModal>
  );
}
